import {html, nothing, type TemplateResult} from 'lit';

/**
 * 出題文字と ○/× のオーバーレイ。
 *
 * ○/× は視覚表現なので aria-hidden にし、正誤は常設の live region で読み上げる。
 * 条件レンダーされる要素に aria-live を付けても、挿入と同時では読まれないことがあるため。
 */
export function characterDisplay(
  character: string | undefined,
  showResult: boolean,
  resultCorrect: boolean | null,
  /** 不正解時に読み上げる対比の説明 */
  feedback = '',
  /**
   * 「次へ」待ちで画面が止まっている状態。
   * ○/× のアニメーションは 1 秒で opacity 0 に着地するので、
   * 止まっている間はアニメーションを使わず静止表示にする。
   */
  persistent = false,
  /**
   * 出題ボックスの内側に絶対配置する常設 UI（進捗ライン・定着バー・チップ）。
   * フローに入れない断片だけを渡すこと。ボックスの高さ h-64 は変えない。
   */
  hud: TemplateResult | typeof nothing = nothing,
  /** 正解時の読み上げ。節目があれば「正解。この字は定着しました」のように文を足す */
  correctText = '正解',
  /** 初めての人向けの 1 行（空なら出さない）。下中央に絶対配置なので高さは変わらない */
  prompt = '',
  /**
   * 下中央に並べる「字 = 読み」。正解の 1.3 秒と、はじめての字の教示（label 付き）で使う。
   * 節目のチップと同じ場所（呼び出し側で排他）
   */
  pair: {char: string; romanized: string; label?: string} | null = null,
  /** 「今日はここまで」の休止中、出題文字の代わりに出すまとめ。nothing なら通常表示 */
  resting: TemplateResult | typeof nothing = nothing,
  /** 読みから字を選ぶ向き。出題文字の代わりに reading を大きく出す */
  reversed = false,
  reading = '',
  /** 結果表示中でないときに live region へ流す文（はじめての字の教示） */
  idleText = '',
  /**
   * はじめての字の問題のあいだ、出題文字を 20px 上げる。下中央の教示（ラベル + 対）は
   * 上端が y≈198px で、text-9xl の下付きマートラーは y≈218px まで伸びて重なる。
   * 問題の途中で動かさない（添えが消えても上げたまま）。transform なので高さは変わらない
   */
  lift = false
): TemplateResult {
  return html`
    <div
      part="character"
      class="relative bg-white rounded-2xl shadow-md ring-1 ring-inset ring-indigo-100 h-64 flex items-center justify-center overflow-hidden font-devanagari"
    >
      ${hud}
      ${prompt
        ? html`<p class="absolute inset-x-0 bottom-3 z-10 px-4 text-center font-sans text-sm text-gray-700">
            ${prompt}
          </p>`
        : nothing}
      ${pair
        ? html`<p
            aria-hidden="true"
            class="absolute inset-x-0 bottom-3 z-10 text-center font-sans text-base text-indigo-900"
          >
            ${pair.label
              ? html`<span class="block text-xs text-gray-600 mb-0.5">${pair.label}</span>`
              : nothing}
            <span class="inline-block rounded-full bg-white/90 px-3 py-0.5"
              ><span lang="sa-Deva" class="font-devanagari">${pair.char}</span> =
              <span lang="sa-Latn" class="font-medium">${pair.romanized}</span></span
            >
          </p>`
        : nothing}
      ${showResult
        ? html`<div
            aria-hidden="true"
            class="absolute inset-0 flex items-center justify-center ${persistent
              ? 'opacity-100'
              : 'opacity-0 animate-result-mark'}"
          >
            <!-- 文字グリフの ○ × は system-ui では線が細く小さく、薄くした出題文字の上で
                 走り書きにしか見えなかった。CSS で太い輪と 2 本の斜線を描く -->
            ${resultCorrect
              ? html`<span
                  class="dq-mark block w-40 h-40 rounded-full border-[12px] border-green-700"
                ></span>`
              : html`<span class="relative block w-40 h-40">
                  <span
                    class="dq-mark absolute left-1/2 top-1/2 w-56 h-3 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-red-600"
                  ></span>
                  <span
                    class="dq-mark absolute left-1/2 top-1/2 w-56 h-3 -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-red-600"
                  ></span>
                </span>`}
          </div>`
        : nothing}

      ${resting !== nothing
        ? html`<div id="dq-rest-summary" class="font-sans px-6 text-center">${resting}</div>`
        : character === undefined
        ? html`<p class="font-sans px-8 text-center text-gray-600">
            下の「クイズ設定」で、練習する文字の種類を選んでください
          </p>`
        : reversed
          ? html`<div
              lang="sa-Latn"
              class="font-sans text-6xl sm:text-7xl font-medium text-indigo-900 transition-opacity duration-300 ${showResult
                ? 'opacity-30'
                : 'opacity-100'}"
            >
              ${reading}
            </div>`
        : html`<div
            lang="sa-Deva"
            class="text-8xl sm:text-9xl text-indigo-900 transition-opacity duration-300 ${showResult
              ? 'opacity-30'
              : 'opacity-100'} ${lift ? '-translate-y-5' : ''}"
          >
            ${character}
          </div>`}
    </div>

    <p role="status" aria-live="polite" class="sr-only">
      ${showResult ? (resultCorrect ? correctText : `不正解。${feedback}`) : idleText}
    </p>
  `;
}
